import { useCallback, useEffect, useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type OnConnect,
  addEdge,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Network } from "lucide-react";
import { AgentNode } from "./AgentNode.js";
import { CustomEdge } from "./EdgeComponent.js";
import { CanvasToolbar } from "./CanvasToolbar.js";
import { CanvasBackground } from "./CanvasBackground.js";
import { EmptyState } from "../common/EmptyState.js";
import { useWorldStore } from "../../stores/world.js";
import { useTopology, useAgents } from "../../api/hooks.js";
import { layoutNodes } from "../../lib/topology-layout.js";

const nodeTypes = { agent: AgentNode };
const edgeTypes = { custom: CustomEdge };

/**
 * Converts a topology definition into React Flow nodes and edges.
 */
function topologyToFlow(topology: any, agents: any[]): { nodes: Node[]; edges: Edge[] } {
  if (!topology?.definition) return { nodes: [], edges: [] };

  const def = topology.definition;
  const agentDefs = def.agents ?? [];
  const edgeDefs = def.edges ?? [];

  // Create agent status map
  const agentStatusMap = new Map<string, any>();
  for (const a of agents) {
    agentStatusMap.set(a.agentId, a);
  }

  // Compute topology-aware positions
  const { positions, orientation } = layoutNodes(agentDefs, def);

  const nodes: Node[] = agentDefs.map((agent: any) => {
    const status = agentStatusMap.get(agent.id);
    const pos = positions.get(agent.id) ?? { x: 100, y: 100 };

    return {
      id: agent.id,
      type: "agent",
      position: pos,
      data: {
        label: agent.name ?? agent.id,
        agentId: agent.id,
        backend: agent.backend,
        status: status?.status ?? "idle",
        costUsd: status?.costUsd ?? 0,
        tokenUsage: status?.tokenUsage ?? { input: 0, output: 0 },
        capabilities: agent.capabilities ?? [],
        isEntryPoint: agent.id === def.entryPoint,
        isExitPoint: (def.exitPoints ?? []).includes(agent.id),
        orientation,
      },
    };
  });

  const edges: Edge[] = edgeDefs.map((edge: any, i: number) => ({
    id: `edge-${i}`,
    source: edge.from,
    target: edge.to,
    type: "custom",
    data: {
      edgeType: edge.type,
      bidirectional: edge.bidirectional,
      active: false,
    },
    animated: false,
  }));

  return { nodes, edges };
}

export function TopologyCanvas() {
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const mode = useWorldStore((s) => s.mode);

  const { data: topology } = useTopology(selectedTopologyId ?? "");
  const { data: agents } = useAgents(selectedTopologyId ?? undefined);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Update nodes/edges when topology data changes
  useEffect(() => {
    if (topology) {
      const flow = topologyToFlow(topology, agents ?? []);
      setNodes(flow.nodes);
      setEdges(flow.edges);
    }
  }, [topology, agents, setNodes, setEdges]);

  const onConnect: OnConnect = useCallback(
    (params) => {
      if (mode === "design") {
        setEdges((eds) =>
          addEdge(
            {
              ...params,
              type: "custom",
              data: { edgeType: "pipes-to", bidirectional: false },
            },
            eds,
          ),
        );
      }
    },
    [mode, setEdges],
  );

  const defaultEdgeOptions = useMemo(
    () => ({
      type: "custom",
    }),
    [],
  );

  if (!selectedTopologyId) {
    return (
      <div className="h-full flex items-center justify-center dot-grid-bg">
        <EmptyState
          icon={Network}
          title="Select a topology"
          description="Choose a topology from the sidebar or create one in design mode"
        />
      </div>
    );
  }

  return (
    <div className="h-full w-full relative">
      <CanvasToolbar />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        nodesDraggable={mode === "design"}
        nodesConnectable={mode === "design"}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#27272a" />
        <CanvasBackground topologyType={topology?.definition?.type} />
        <Controls className="!bg-surface-light/80 !backdrop-blur-sm !border-border-subtle/20 !rounded-lg" />
        <MiniMap
          nodeColor="#3f3f46"
          maskColor="rgba(10, 10, 11, 0.8)"
          className="!bg-surface-light/80 !backdrop-blur-sm !border-border-subtle/20 !rounded-lg"
        />
      </ReactFlow>
    </div>
  );
}
